import { Box, Button, CircularProgress, Drawer, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import DetailPanel from "~/components/detailPanel/DetailPanel";
import DetailBottomSheet from "~/components/mobile/DetailBottomSheet";
import RetryableErrorState from "~/components/RetryableErrorState";
import { BodyText, BodyTextSmall, CaptionText, SubtitleText, TitleText } from "~/components/Typographies";
import { buildUsedByRefRoute, type UsedByRef, type UsedBySubject } from "~/components/usedBy/usedByHelpers";
import useUsedBy from "~/components/usedBy/useUsedBy";
import { useAppSelector } from "~/hooks/reduxHooks";

interface UsedByPanelProps {
	onClose: () => void;
	subject: null | UsedBySubject;
}

interface UsedByRowProps {
	onOpen: (route: string) => void;
	usedByRef: UsedByRef;
}

interface UsedByListProps {
	onClose: () => void;
	subject: UsedBySubject;
}

const UsedByRow = ({ onOpen, usedByRef }: UsedByRowProps) => {
	const { t } = useTranslation("common");
	const route = buildUsedByRefRoute(usedByRef);
	const text = (
		<ListItemText
			primary={usedByRef.name}
			secondary={[usedByRef.parent?.name, usedByRef.status].filter(Boolean).join(" · ")}
		/>
	);

	if (!route) {
		return <ListItem>{text}</ListItem>;
	}

	return (
		<ListItemButton aria-label={t("usedBy.openRecord", { name: usedByRef.name })} onClick={() => onOpen(route)}>
			{text}
		</ListItemButton>
	);
};

const UsedByList = ({ onClose, subject }: UsedByListProps) => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();
	const { groups, isError, isLoading, loadingMoreKind, refetch, showMore, total } = useUsedBy(subject);

	const handleOpenRecord = (route: string) => {
		onClose();
		navigate(route);
	};

	if (isLoading) {
		return <CircularProgress aria-label={t("usedBy.loading")} size={24} />;
	}

	if (isError) {
		return <RetryableErrorState dense message={t("usedBy.error", { name: subject.name })} onRetry={refetch} />;
	}

	if (total === 0) {
		return <BodyText>{t("usedBy.empty")}</BodyText>;
	}

	return (
		<>
			<BodyTextSmall color="text.secondary" sx={{ pb: 1 }}>
				{t("usedBy.total", { count: total })}
			</BodyTextSmall>
			{groups.map((group) => (
				<Box key={group.kind} sx={{ pb: 1.5 }}>
					<SubtitleText bold>
						{t("usedBy.groupHeading", { count: group.total, kind: t(`usedBy.kinds.${group.kind}`) })}
					</SubtitleText>
					<List dense disablePadding>
						{group.refs.map((ref) => (
							<UsedByRow key={`${ref.kind}:${ref.id}`} onOpen={handleOpenRecord} usedByRef={ref} />
						))}
					</List>
					{group.hidden > 0 && (
						<CaptionText color="text.secondary" sx={{ px: 2 }}>
							{t("usedBy.hidden", { count: group.hidden })}
						</CaptionText>
					)}
					{group.hasMore && (
						<Button disabled={loadingMoreKind === group.kind} onClick={() => showMore(group.kind)} size="small">
							{t("usedBy.showMore")}
						</Button>
					)}
				</Box>
			))}
		</>
	);
};

const UsedByContent = ({ onClose, subject }: UsedByListProps) => {
	const { t } = useTranslation("common");

	return (
		<Box sx={{ height: "100%", maxWidth: "100%", width: { md: 420, xs: "100%" } }}>
			<DetailPanel
				hideSingleTabStrip
				onClose={onClose}
				title={
					<Box sx={{ minWidth: 0 }}>
						<CaptionText color="text.secondary">{t("usedBy.title")}</CaptionText>
						<TitleText bold noWrap>
							{subject.name}
						</TitleText>
					</Box>
				}
			>
				<UsedByList onClose={onClose} subject={subject} />
			</DetailPanel>
		</Box>
	);
};

const UsedByPanel = ({ onClose, subject }: UsedByPanelProps) => {
	const isWideScreen = useAppSelector((state) => state.layout.isWideScreen);
	const content = subject && (
		<UsedByContent key={`${subject.kind}:${subject.id}`} onClose={onClose} subject={subject} />
	);

	if (isWideScreen) {
		return (
			<Drawer anchor="right" onClose={onClose} open={!!subject} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
				{content}
			</Drawer>
		);
	}
	return (
		<DetailBottomSheet onClose={onClose} open={!!subject}>
			{content}
		</DetailBottomSheet>
	);
};

export default UsedByPanel;
